import { resolve } from 'inversify-react';
import get from 'lodash/get';
import pluralize from 'pluralize-ru';
import { rgba } from 'polished';
import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import styled from 'styled-components';
import COLORS from '../../../constants/COLORS';
import { STATUS_ICONS_NAMES } from '../../../constants/STATUS_ICONS';
import { createOutlines } from '../../../helpers/svg';
import transliterate from '../../../helpers/transliterate';
import { BackgroundController } from '../../../lib/widget/background/background.controller';
import type { IBackgroundController } from '../../../lib/widget/background/background.interface';
import { BACKGROUND_MODULE_IDS } from '../../../lib/widget/background/background.types';
import type { IPopoverController } from '../../../lib/widget/popover/popover.interface';
import { POPOVER_MODULE_IDS } from '../../../lib/widget/popover/popover.types';
import type { IStateController } from '../../../lib/widget/state/state.interface';
import { STATE_MODULE_IDS } from '../../../lib/widget/state/state.types';
import type { ITicketsController } from '../../../lib/widget/tickets/tickets.interface';
import { TICKETS_MODULE_IDS } from '../../../lib/widget/tickets/tickets.types';
import type { IZoomController } from '../../../lib/widget/zoom/zoom.interface';
import { ZOOM_MODULE_IDS } from '../../../lib/widget/zoom/zoom.types';
import { getBoundingClientRect } from '../../../helpers/coordinates';

function getStrokeWidth() {
  return Math.round((2 / window.Widget.zoomController.currentZoom) * window.Widget.dpr).toString();
}

interface BackgroundState {
  isLoading: boolean;
}

interface BackgroundProps {
  previewMode?: boolean;
}

class Background extends Component<BackgroundProps & WithTranslation, BackgroundState> {
  svgNode: any;

  focusedSector: HTMLElement | null = null;

  focusedPaths: Array<SVGPathElement> = [];

  focusedCategory: string | null = null;

  updated: boolean = false;

  sector: any;

  baseWidth: number;

  baseHeight: number;

  svgContainer: HTMLElement | null;

  backgroundController: IBackgroundController;
  stateController: IStateController;
  zoomController: IZoomController;
  ticketsController: ITicketsController;
  popoverController: IPopoverController;

  @resolve(BackgroundController)
  BackgroundController;

  constructor(props: any, container) {
    super(props);

    this.state = {
      isLoading: true,
    };
    this.backgroundController = container.get(BACKGROUND_MODULE_IDS.BACKGROUND_CONTROLLER);
    this.stateController = container.get(STATE_MODULE_IDS.STATE_CONTROLLER);
    this.zoomController = container.get(ZOOM_MODULE_IDS.ZOOM_CONTROLLER);
    this.ticketsController = container.get(TICKETS_MODULE_IDS.TICKETS_CONTROLLER);
    this.popoverController = container.get(POPOVER_MODULE_IDS.POPOVER_CONTROLLER);

    this.stateController.addListener(this.stateController.events.UPDATE, this.onUpdate);
    this.stateController.addListener(
      this.stateController.events.TICKETS_DATA_LOADED,
      this.renderBackground,
    );
    this.backgroundController.addListener(
      this.backgroundController.events.UPDATE,
      this.updateBackground,
    );
    this.backgroundController.addListener(
      this.backgroundController.events.FORCE_UPDATE,
      this.forceUpdateBackground,
    );
  }

  componentDidMount() {
    this.svgContainer = document.getElementById('background');
  }

  shouldComponentUpdate() {
    return false;
  }

  renderBackground = () => {
    const { previewMode } = this.props;
    const { outlines } = this.backgroundController;

    if (this.svgNode) {
      this.svgContainer?.removeChild(this.svgNode);
    }

    if (previewMode || !window.Widget.schemeParameters.hallLayoutMeta) return null;

    const { width, height } = window.Widget.schemeParameters.hallLayoutMeta;

    const { svg: svgNode, categoriesPaths } = createOutlines('background', outlines, {
      width,
      height,
    });

    window.Widget.categoriesController.categoriesPaths = categoriesPaths;

    svgNode.style.position = 'absolute';
    svgNode.style.opacity = '0';
    this.svgNode = svgNode;

    [].forEach.call(svgNode.querySelectorAll('path'), (pathNode: any) => {
      pathNode.addEventListener('mouseenter', this.showSector);
      pathNode.addEventListener('mouseleave', this.hideSector);

      pathNode.d = pathNode.getAttribute('d');
      pathNode.bbox = pathNode.getBBox();
    });

    this.updateBackground();
  };

  updateBackground = () => {
    const { a: scaleX, d: scaleY, e: tx, f: ty } = window.Widget.staticCtx.getTransform();
    const { dpr } = window.Widget;
    if (this.svgNode && !this.updated) {
      const height = this.baseHeight || this.svgNode.height.baseVal.value;
      const width = this.baseWidth || this.svgNode.width.baseVal.value;

      this.svgNode.style.opacity = '1';
      this.svgNode.style.transform = 'none';
      this.svgNode.style.transition = 'none';
      const left = `${tx / dpr}px`;
      const top = `${ty / dpr}px`;
      this.svgNode.style.left = left;
      this.svgNode.style.top = top;

      const nodeWidth = (width * scaleX) / dpr;
      const nodeHeight = (height * scaleY) / dpr;
      if (nodeWidth > 0) this.svgNode.setAttribute('width', nodeWidth);
      if (nodeHeight > 0) this.svgNode.setAttribute('height', nodeHeight);
      window.Widget.svgNode = this.svgNode;

      if (!this.baseHeight) {
        this.baseHeight = height;
        this.baseWidth = width;
      }
    }
  };

  forceUpdateBackground = () => {
    const { a: scaleX, d: scaleY, e: tx, f: ty } = window.Widget.staticCtx.getTransform();
    const { dpr } = window.Widget;

    if (this.svgNode) {
      const height = this.baseHeight;
      const width = this.baseWidth;

      this.svgNode.style.opacity = '1';
      this.svgNode.style.transform = 'none';
      this.svgNode.style.left = `${tx / dpr}px`;
      this.svgNode.style.top = `${ty / dpr}px`;

      const nodeWidth = (width * scaleX) / dpr;
      const nodeHeight = (height * scaleY) / dpr;
      if (nodeWidth > 0) this.svgNode.setAttribute('width', (width * scaleX) / dpr);
      if (nodeHeight > 0) this.svgNode.setAttribute('height', (height * scaleY) / dpr);

      if (!this.baseHeight) {
        this.baseHeight = this.svgNode.height.baseVal.value;
        this.baseWidth = this.svgNode.width.baseVal.value;
      }
    }
  };

  onUpdate = ({ isLoading, ticketsIsLoaded, isDragging }) => {
    if (isDragging && this.focusedSector) {
      this.clearSectorInCanvas();
    }

    if (ticketsIsLoaded && !isDragging) {
      this.renderBackground();
    }

    if (this.state.isLoading !== isLoading) {
      this.setState(() => ({
        isLoading,
      }));
    }
  };

  showSector = (event) => {
    const { i18n } = this.props;

    const { target } = event;
    const { isDragging, isLoading } = this.stateController;
    const { zoomIsMinimum } = this.zoomController;

    if (isDragging || !zoomIsMinimum || isLoading) return;

    this.focusedSector = target;

    if (this.focusedSector) {
      this.clearSectorInCanvas();
    }

    const sectorId = target.getAttribute('data-id');
    const isCategory = target.getAttribute('category') === 'true';

    if (isCategory) {
      return this.highlightCategory(target);
    }

    this.sector = this.ticketsController.findSector(sectorId);

    const bbox = getBoundingClientRect(target);
    const { left, top, width } = bbox;
    const { label } = this.sector;
    let { ticketsCount } = this.sector;
    const selectedTicket = this.ticketsController.selectedTickets.find(
      (ticket) => ticket.id === sectorId,
    );

    if (selectedTicket) {
      ticketsCount -= selectedTicket.count;
    }

    let priceText;
    let sectorTitle = transliterate(label);

    let availableSeats;
    if (ticketsCount) {
      switch (i18n.language) {
        case 'ru':
          availableSeats = pluralize(
            ticketsCount,
            '',
            '%d свободное место',
            '%d свободных места',
            '%d свободных мест',
          );
          break;
        default:
          availableSeats = `${ticketsCount} ${this.props.t('availableSeats')}`;
          break;
      }
    }

    if (ticketsCount) {
      const { minPrice, maxPrice, prices } = this.sector;

      if (prices) {
        priceText = prices.map((price) => `${price}`);
      } else {
        priceText = minPrice !== maxPrice ? `${minPrice} - ${maxPrice}` : `${minPrice}`;
      }
    }

    const statusIcon = this.sector.at.map((sectorAt) => {
      if (sectorAt === 1) {
        return STATUS_ICONS_NAMES.AVAILABLE;
      }
      if (sectorAt === 2) {
        return STATUS_ICONS_NAMES.PREORDER;
      }
    });

    const { offsetLeft = 0 } = window.Widget;

    this.popoverController.show({
      x: left + width / 2 - offsetLeft,
      y: top - window.Widget.offsetTop,
      title: sectorTitle,
      subtitle: availableSeats,
      text: ticketsCount ? sectorTitle : this.props.t('noAvailableSeats'),
      price: priceText,
      isCategory: !!availableSeats,
      statusIcon,
      ticketType: this.sector.tt,
      offset: 15,
    });

    this.focusedSector.style.stroke = COLORS.YELLOW;
    this.focusedSector.style.strokeWidth = getStrokeWidth();
  };

  hideSector = () => {
    if (this.focusedSector) {
      this.clearSectorInCanvas();
      this.focusedSector = null;
      this.sector = null;

      if (!this.popoverController.state.isHidden && !this.sector) {
        this.popoverController.hide();
      }
    }

    if (this.focusedPaths.length) {
      [].forEach.call(this.focusedPaths, (pathEl) => {
        pathEl.style.fill = 'rgba(0, 0, 0, 0)';
      });
      this.focusedPaths = [];
      this.focusedCategory = null;
    }
  };

  clearSectorInCanvas = () => {
    const { fillOpacity } = this.focusedSector.style;
    const { fill } = this.focusedSector.style;
    this.focusedSector?.removeAttribute('style');
    this.focusedSector.style.fillOpacity = fillOpacity;
    this.focusedSector.style.fill = fill;
  };

  highlightCategory = (target) => {
    const { i18n } = this.props;

    const id = target.getAttribute('data-id');
    const currentCategory = window.Widget.categoriesController.categories[id];
    const paths = window.Widget.categoriesController.categoriesPaths[id];
    this.focusedPaths = paths;
    this.focusedCategory = id;

    [].forEach.call(this.focusedPaths, (pathEl) => {
      pathEl.style.fill = rgba(currentCategory.color, 0.32);
    });

    const bbox = getBoundingClientRect(target);
    const { left, top, width } = bbox;
    const { label, count, price } = currentCategory;
    const selectedTicketsCount = get(
      window.Widget.categoriesController.selectedCategories[id],
      'count',
      0,
    );
    let ticketsCount = count;

    if (selectedTicketsCount) {
      ticketsCount -= selectedTicketsCount;
    }

    let sectorTitle = transliterate(label);
    let availableSeats;

    if (count) {
      switch (i18n.language) {
        case 'ru':
          availableSeats = pluralize(
            ticketsCount,
            '',
            '%d свободное место',
            '%d свободных места',
            '%d свободных мест',
          );
          break;
        default:
          availableSeats = `${ticketsCount} ${this.props.t('availableSeats')}`;
          break;
      }
    }

    const { offsetLeft = 0 } = window.Widget;
    this.popoverController.show({
      x: left + width / 2 - offsetLeft,
      y: top - window.Widget.offsetTop,
      title: sectorTitle,
      text: count ? sectorTitle : this.props.t('noAvailableSeats'),
      price,
      statusIcon: count
        ? currentCategory.at === 1
          ? [STATUS_ICONS_NAMES.AVAILABLE]
          : [STATUS_ICONS_NAMES.PREORDER]
        : [],
      ticketType: count ? [currentCategory.tt] : [],
      subtitle: availableSeats,
      isCategory: !!availableSeats,
      offset: 15,
    });
  };

  render() {
    return <Container onClick={this.hideSector} id="background" />;
  }
}

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

// @ts-ignore
export default withTranslation()(Background);
